import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import {
  DailyCronDataAPI,
  DailyCronPaymentsAPI,
  pfStatusEventAPI,
  pfStatusEventAPIItem,
  DailyCronDataAPIPayload,
  pfStatusEventAPIPayload,
  dailyCronData,
} from '../interface/daily-cron-api.interface';
import { environment } from '../../../../../environments/environment';

/**
 * Enterprise-grade API service for Daily Cron operations.
 * Provides typed methods for interacting with daily cron payment and status event endpoints.
 * 
 * @class DailyCronApiService
 */
@Injectable({
  providedIn: 'root',
})
export class DailyCronApiService {
  private readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(
    private apiService: ApiService,
    private productConfigService: ProductConfigService
  ) { }

  private getProductContext() {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');

    // Default to 'denefits' if not found (though component should now set it)
    const effectiveProductId = productId || 'denefits';
    const productName = this.productConfigService.getProductName(effectiveProductId);
    const config = this.productConfigService.getProductConfig(productName);

    let productServerName = config.productServerName;
    // Legacy support for Denefits non-prod
    // if (['denefits', 'credee'].includes(config.id) && !environment.production) {
    //   productServerName = `${config.id}-live`;
    // }

    return {
      config,
      productServerName,
      toolsPath: config.reportToolsPath || 'visionboard/tools'
    };
  }

  private mapResponse(response: any) {
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid response format from daily cron payments API');
    }

    if (response.is_error == 1) {
      console.error('Error retrieving data:', response);
      throw new Error('something went wrong');
    }

    return response.hasOwnProperty('data') ? response.data : response;
  }

  getDailyCronPayments(
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<DailyCronPaymentsAPI> {
    const {
      showLoader = false,
      cache = true,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;
    const { config, productServerName, toolsPath } = this.getProductContext();

    return this.apiService
      .post<DailyCronPaymentsAPI>(
        `${toolsPath}/daily_cron_payments`,
        { ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName: productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve daily cron payments',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving daily cron payments';
          console.error('Error retrieving daily cron payments:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  getDailyCronData(
    payload: DailyCronDataAPIPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<DailyCronDataAPI> {
    const {
      showLoader = true,
      cache = true,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;
    const { config, productServerName, toolsPath } = this.getProductContext();

    payload = Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '').reduce((obj, [key, value]) => {
      obj[key as keyof any] = value as any;
      return obj;
    }, {} as any);

    return this.apiService
      .post<DailyCronDataAPI>(
        `${toolsPath}/daily_cron_data`,
        { ...payload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName: productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve daily cron data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);

          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }

          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving daily cron data';
          console.error('Error retrieving daily cron data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  getMostRecentDailyCronData(options: {
    showLoader?: boolean;
    cache?: boolean | number;
    showErrorToaster?: boolean;
  } = {}): Observable<dailyCronData> {
    const {
      showLoader = true,
      cache = true,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;
    const { config, productServerName, toolsPath } = this.getProductContext();

    return this.apiService.post<dailyCronData>(
      `${toolsPath}/daily_cron_most_recent_attempt`,
      { ...config.payloadExtras },
      {
        addAccessToken: config.requiresAccessToken,
        cache: cache ? cacheTTL : false,
        productServerName: productServerName,
        showLoader,
        showErrorToaster,
        errorMessage: 'Failed to retrieve most recent daily cron data',
      }
    )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving most recent daily cron data';
          console.error('Error retrieving most recent daily cron data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  getDailyCronOverview(options: {
    showLoader?: boolean;
    cache?: boolean | number;
    showErrorToaster?: boolean;
  }, payload: any): Observable<DailyCronDataAPI> {
    const {
      showLoader = true,
      cache = true,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;
    const { config, productServerName, toolsPath } = this.getProductContext();

    return this.apiService.post<DailyCronDataAPI>(
      `${toolsPath}/daily_cron_overview`,
      { ...config.payloadExtras },
      {
        addAccessToken: config.requiresAccessToken,
        cache: cache ? cacheTTL : false,
        productServerName: productServerName,
        showLoader,
        showErrorToaster,
        errorMessage: 'Failed to retrieve most recent daily cron data',
      }
    )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving most recent daily cron data';
          console.error('Error retrieving most recent daily cron data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves PF status event data based on the provided payload.
   * 
   * @param {pfStatusEventAPIPayload} payload - The request payload containing filter criteria
   * @param {Object} options - Optional configuration for the API request
   * @param {boolean} options.showLoader - Whether to show loading indicator (default: true)
   * @param {boolean} options.cache - Whether to cache the response (default: true)
   * @param {number} options.cacheTTL - Cache time-to-live in milliseconds (default: 5 minutes)
   * @param {boolean} options.showErrorToaster - Whether to show error toaster (default: true)
   * @returns {Observable<pfStatusEventAPI>} Observable that emits the PF status event data array
   * 
   * @example
   * ```typescript
   * this.dailyCronApiService.getPfStatusEvent({
   *   old_pf_status: "1, 2",
   *   new_pf_status: "4",
   *   start_time: "2024-01-01",
   *   end_time: "2024-01-31",
   *   unit: "month"
   * }).subscribe({
   *   next: (data) => console.log('Status Events:', data),
   *   error: (error) => console.error('Error:', error)
   * });
   * ```
   */
  getPfStatusEvent(
    payload: pfStatusEventAPIPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<pfStatusEventAPI> {
    const {
      showLoader = true,
      cache = true,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;
    const { config, productServerName, toolsPath } = this.getProductContext();

    return this.apiService
      .post<pfStatusEventAPI>(
        `${toolsPath}/pf_status_event`,
        { ...payload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName: productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve PF status event data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          // Validate response structure
          if (!response) {
            throw new Error('Invalid response format from PF status event API');
          }
          // Ensure response is an array (based on interface definition)
          if (!Array.isArray(response)) {
            throw new Error('Invalid response structure: expected array of status events');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving PF status event data';
          console.error('Error retrieving PF status event data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Clears the cache for all daily cron related endpoints.
   * Useful when data needs to be refreshed immediately.
   * 
   * @param {string} endpoint - Optional specific endpoint to clear cache for
   * 
   * @example
   * ```typescript
   * // Clear cache for all daily cron endpoints
   * this.dailyCronApiService.clearCache();
   * 
   * // Clear cache for specific endpoint
   * this.dailyCronApiService.clearCache('daily_cron_payments0');
   * ```
   */
  clearCache(endpoint?: string): void {
    const { toolsPath } = this.getProductContext();
    if (endpoint) {
      this.apiService.clearCache(`${toolsPath}/${endpoint}`);
    } else {
      // Clear cache for all daily cron endpoints
      this.apiService.clearCache(`${toolsPath}/daily_cron_payments`);
      this.apiService.clearCache(`${toolsPath}/daily_cron_data`);
      this.apiService.clearCache(`${toolsPath}/pf_status_event`);
    }
  }
}
